// ไฟล์: frontend/src/pages/SearchPage.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './SearchPage.css';

const SearchPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  
  const [searchResults, setSearchResults] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [gradeLevels, setGradeLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchStats, setSearchStats] = useState({
    total: 0,
    resources: 0,
    innovations: 0,
    articles: 0,
  });
  
  // ดึงค่าจาก URL
  const [filters, setFilters] = useState({
    query: queryParams.get('query') || '',
    category: queryParams.get('category') || '',
    subject: queryParams.get('subject') || '',
    gradeLevel: queryParams.get('gradeLevel') || '',
    type: queryParams.get('type') || 'all'
  });

  // ดึงข้อมูลตัวกรอง
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        // ดึงข้อมูลหมวดหมู่
        const categoriesResponse = await axios.get('/categories');
        setCategories(categoriesResponse.data.categories);
        
        // ดึงข้อมูลวิชา
        const subjectsResponse = await axios.get('/subjects');
        setSubjects(subjectsResponse.data.subjects);
        
        // ดึงข้อมูลระดับชั้น
        const gradeLevelsResponse = await axios.get('/grade-levels');
        setGradeLevels(gradeLevelsResponse.data.gradeLevels);
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };

    fetchFilterOptions();
  }, []);

  // ค้นหาเมื่อ filters เปลี่ยนแปลง
  useEffect(() => {
    const searchItems = async () => {
      setLoading(true);
      
      try {
        // สร้าง query string
        const params = new URLSearchParams();
        
        if (filters.query) params.append('query', filters.query);
        if (filters.category) params.append('category', filters.category);
        if (filters.subject) params.append('subject', filters.subject);
        if (filters.gradeLevel) params.append('gradeLevel', filters.gradeLevel);
        
        // ถ้าไม่มีการค้นหา ให้แสดงผลว่าง
        if (params.toString() === '') {
          setSearchResults([]);
          setSearchStats({
            total: 0,
            resources: 0,
            innovations: 0,
            articles: 0,
          });
          setLoading(false);
          return;
        }
        
        const response = await axios.get(`/search?${params.toString()}`);
        
        let results = response.data.results;
        
        // กรองตามประเภทถ้าไม่ใช่ 'all'
        if (filters.type !== 'all') {
          results = results.filter(item => item.type === filters.type);
        }
        
        setSearchResults(results);
        
        // คำนวณสถิติ
        const stats = {
          total: results.length,
          resources: results.filter(item => item.type === 'resource').length,
          innovations: results.filter(item => item.type === 'innovation').length,
          articles: results.filter(item => item.type === 'article').length,
        };
        
        setSearchStats(stats);
      } catch (error) {
        console.error('Error searching items:', error);
      } finally {
        setLoading(false);
      }
    };

    // อัพเดท URL
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    
    navigate(`/search?${params.toString()}`, { replace: true });
    
    searchItems();
  }, [filters, navigate]);

  // จัดการการเปลี่ยนแปลงของฟอร์ม
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // รีเซ็ตตัวกรอง
  const resetFilters = () => {
    setFilters({
      query: '',
      category: '',
      subject: '',
      gradeLevel: '',
      type: 'all'
    });
  };

  // จัดการการค้นหา
  const handleSearch = (e) => {
    e.preventDefault();
    // การค้นหาจะเกิดขึ้นอัตโนมัติผ่าน useEffect
  };

  // รับลิงก์ของ item
  const getItemLink = (item) => {
    if (item.type === 'resource') return `/resources/${item.id}`;
    if (item.type === 'innovation') return `/innovations/${item.id}`;
    if (item.type === 'article') return `/articles/${item.id}`;
    return '#';
  };

  // รับชื่อไอคอนตาม item type
  const getItemTypeIcon = (type) => {
    if (type === 'resource') return 'fas fa-book';
    if (type === 'innovation') return 'fas fa-lightbulb';
    if (type === 'article') return 'fas fa-pen-fancy';
    return 'fas fa-file';
  };

  // รับชื่อของ item type
  const getItemTypeName = (type) => {
    if (type === 'resource') return 'สื่อการสอน';
    if (type === 'innovation') return 'นวัตกรรม';
    if (type === 'article') return 'บทความ';
    return '';
  };

  return (
    <div className="search-page">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">
            <i className="fas fa-search"></i> ค้นหา
          </h1>
          <p className="page-subtitle">ค้นหาสื่อการสอน นวัตกรรม และบทความที่คุณสนใจ</p>
        </div>

        <div className="search-container">
          <div className="search-sidebar">
            <div className="search-filter-card">
              <div className="search-filter-header">
                <h3>ตัวกรองการค้นหา</h3>
                <button type="button" className="btn-reset" onClick={resetFilters}>
                  <i className="fas fa-undo"></i> รีเซ็ต
                </button>
              </div>
              
              <form onSubmit={handleSearch}>
                <div className="form-group">
                  <label htmlFor="query">คำค้นหา</label>
                  <div className="search-input">
                    <i className="fas fa-search"></i>
                    <input
                      type="text"
                      id="query"
                      name="query"
                      className="form-control"
                      placeholder="พิมพ์คำค้นหา..."
                      value={filters.query}
                      onChange={handleFilterChange}
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="type">ประเภท</label>
                  <select
                    id="type"
                    name="type"
                    className="form-select"
                    value={filters.type}
                    onChange={handleFilterChange}
                  >
                    <option value="all">ทั้งหมด</option>
                    <option value="resource">สื่อการสอน</option>
                    <option value="innovation">นวัตกรรม</option>
                    <option value="article">บทความ</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="category">หมวดหมู่</label>
                  <select
                    id="category"
                    name="category"
                    className="form-select"
                    value={filters.category}
                    onChange={handleFilterChange}
                  >
                    <option value="">ทุกหมวดหมู่</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="subject">วิชา</label>
                  <select
                    id="subject"
                    name="subject"
                    className="form-select"
                    value={filters.subject}
                    onChange={handleFilterChange}
                  >
                    <option value="">ทุกวิชา</option>
                    {subjects.map(subject => (
                      <option key={subject.id} value={subject.id}>
                        {subject.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="gradeLevel">ระดับชั้น</label>
                  <select
                    id="gradeLevel"
                    name="gradeLevel"
                    className="form-select"
                    value={filters.gradeLevel}
                    onChange={handleFilterChange}
                  >
                    <option value="">ทุกระดับชั้น</option>
                    {gradeLevels.map(level => (
                      <option key={level.id} value={level.id}>
                        {level.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <button type="submit" className="btn btn-primary btn-block">
                  <i className="fas fa-search"></i> ค้นหา
                </button>
              </form>
            </div>
            
            <div className="search-stats-card">
              <h3>ผลการค้นหา</h3>
              <div className="search-stat">
                <div className="stat-icon">
                  <i className="fas fa-search"></i>
                </div>
                <div className="stat-content">
                  <div className="stat-value">{searchStats.total}</div>
                  <div className="stat-label">ทั้งหมด</div>
                </div>
              </div>
              
              <div className="search-stat">
                <div className="stat-icon">
                  <i className="fas fa-book"></i>
                </div>
                <div className="stat-content">
                  <div className="stat-value">{searchStats.resources}</div>
                  <div className="stat-label">สื่อการสอน</div>
                </div>
              </div>
              
              <div className="search-stat">
                <div className="stat-icon">
                  <i className="fas fa-lightbulb"></i>
                </div>
                <div className="stat-content">
                  <div className="stat-value">{searchStats.innovations}</div>
                  <div className="stat-label">นวัตกรรม</div>
                </div>
              </div>
              
              <div className="search-stat">
                <div className="stat-icon">
                  <i className="fas fa-pen-fancy"></i>
                </div>
                <div className="stat-content">
                  <div className="stat-value">{searchStats.articles}</div>
                  <div className="stat-label">บทความ</div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="search-results">
            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner">กำลังค้นหา...</div>
              </div>
            ) : (
              <>
                {!filters.query && !filters.category && !filters.subject && !filters.gradeLevel ? (
                  <div className="search-empty">
                    <div className="empty-icon">
                      <i className="fas fa-search"></i>
                    </div>
                    <h3>ระบุคำค้นหาหรือตัวกรอง</h3>
                    <p>กรุณาระบุคำค้นหาหรือเลือกตัวกรองเพื่อค้นหาสื่อการสอน นวัตกรรม และบทความ</p>
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="search-empty">
                    <div className="empty-icon">
                      <i className="fas fa-search"></i>
                    </div>
                    <h3>ไม่พบผลการค้นหา</h3>
                    <p>ไม่พบผลการค้นหาตามเงื่อนไขที่คุณระบุ กรุณาลองค้นหาด้วยคำค้นอื่นหรือปรับตัวกรอง</p>
                  </div>
                ) : (
                  <div className="search-results-container">
                    <div className="search-results-header">
                      <h2>ผลการค้นหา "{filters.query}"</h2>
                      <p>พบ {searchResults.length} รายการ</p>
                    </div>
                    
                    <div className="results-grid">
                      {searchResults.map((item, index) => (
                        <div className="result-card" key={`${item.type}-${item.id}-${index}`}>
                          <div className="result-thumbnail">
                            <img 
                              src={item.thumbnail ? `${process.env.REACT_APP_API_URL}/uploads/thumbnails/${item.thumbnail}` : '/assets/default-thumbnail.jpg'} 
                              alt={item.title} 
                            />
                            <div className="result-type">
                              <i className={getItemTypeIcon(item.type)}></i> {getItemTypeName(item.type)}
                            </div>
                          </div>
                          
                          <div className="result-content">
                            <h3 className="result-title">
                              <Link to={getItemLink(item)}>{item.title}</Link>
                            </h3>
                            
                            <div className="result-description">
                              {item.description?.length > 150 
                                ? `${item.description.substring(0, 150)}...` 
                                : item.description}
                            </div>
                            
                            <div className="result-meta">
                              <div className="result-author">
                                <i className="fas fa-user"></i> {item.author_name}
                              </div>
                              {item.category_name && (
                                <div className="result-category">
                                  <i className="fas fa-folder"></i> {item.category_name}
                                </div>
                              )}
                              {item.subject_name && (
                                <div className="result-subject">
                                  <i className="fas fa-book-open"></i> {item.subject_name}
                                </div>
                              )}
                              {item.grade_level_name && (
                                <div className="result-grade">
                                  <i className="fas fa-graduation-cap"></i> {item.grade_level_name}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="result-footer">
                            <Link to={getItemLink(item)} className="btn btn-primary btn-sm">
                              <i className="fas fa-info-circle"></i> ดูรายละเอียด
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
